import { Button, Chip, CircularProgress } from '@sortlist-frontend/design-system';
import { useState } from 'react';

import { useCopyToClipboard } from '../hooks/useCopyToClipboard';
import type { GuideField, GuideLabels } from '../types';
import { NumberField } from './NumberField';
import { ToggleItem } from './ToggleItem';

interface PromptPanelProps {
  fields: readonly (GuideField & { fallback: string })[];
  templates: readonly { kind: string; text: string }[];
  copyLabel: string;
  listLabel: string;
  rateLabel: string;
  labels: Pick<GuideLabels, 'copied' | 'copyFallback'>;
}

export const PromptPanel = (props: PromptPanelProps) => {
  const { fields, templates, copyLabel, listLabel, rateLabel, labels } = props;
  const [values, setValues] = useState<Record<string, string>>({});
  const [cited, setCited] = useState<readonly boolean[]>(templates.map(() => false));
  const { label, copy } = useCopyToClipboard({ idle: copyLabel, copied: labels.copied, fallback: labels.copyFallback });
  const variables = Object.fromEntries(
    fields.map((field) => [field.id.replace('pp-', ''), values[field.id]?.trim() || field.fallback]),
  );
  const prompts = templates.map((template) =>
    template.text.replace(/\{(\w+)\}/g, (_match, name: string) => variables[name] ?? `{${name}}`),
  );
  const count = cited.filter(Boolean).length;
  const rate = Math.round((count / templates.length) * 100);

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[1fr_1.3fr]">
      <div className="space-y-3">
        {fields.map((field) => (
          <NumberField
            key={field.id}
            field={field}
            value={values[field.id] ?? ''}
            onChange={(value) => setValues({ ...values, [field.id]: value })}
            isText
          />
        ))}
        <div
          className="flex items-center gap-4 rounded-2xl border border-secondary-200 bg-white p-4"
          aria-live="polite">
          <CircularProgress
            aria-label={`${rate} %`}
            value={(count / templates.length) * 100}
            color="primary"
            size="xl"
          />
          <div>
            <p className="text-2xl font-extrabold text-secondary-950">{rate} %</p>
            <p className="text-xs text-secondary-500">
              {rateLabel.replace('{n}', String(count)).replace('{t}', String(templates.length))}
            </p>
          </div>
        </div>
        <Button
          color="primary"
          variant="bordered"
          size="md"
          onPress={() => void copy(prompts.map((prompt, index) => `${index + 1}. ${prompt}`).join('\n'))}>
          {label}
        </Button>
      </div>
      <ul className="space-y-3" aria-label={listLabel}>
        {templates.map((template, index) => (
          <li key={template.text}>
            <ToggleItem
              checked={cited[index]}
              onChange={(value) => setCited(cited.map((current, i) => (i === index ? value : current)))}>
              <Chip color="secondary" variant="flat" radius="full" size="sm" className="mr-2">
                {template.kind}
              </Chip>
              {prompts[index]}
            </ToggleItem>
          </li>
        ))}
      </ul>
    </div>
  );
};
